// First-run, dismissible nudge to install the PWA on Chromium (Android, desktop,
// ChromeOS) — Chrome's own beforeinstallprompt UI is just a small, easy-to-miss
// address-bar icon, so this surfaces an explicit "Install"/"Not now" choice instead.
// Shows at most once per browser (localStorage), and only once the browser has
// actually handed usePwaInstallPrompt a beforeinstallprompt event to act on. The
// "Install App" button in MySettings (src/components/MySettings.tsx) uses the same
// hook and stays available afterward for anyone who dismissed this dialog.
import React, { useState } from 'react';
import { Dialog, DialogFooter, DialogType, DefaultButton, PrimaryButton } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { usePwaInstallPrompt } from '../SharedCommon/pwaInstall';

const SEEN_KEY = 'pwa_install_dialog_seen_v1';

function hasSeenDialog(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return true; // storage blocked -> never nag
  }
}

function markDialogSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    // ignore -- storage blocked
  }
}

const PwaInstallDialog: React.FC = () => {
  const { t } = useTranslation();
  const { canInstall, promptInstall } = usePwaInstallPrompt();
  const [dismissed, setDismissed] = useState(false);

  if (!canInstall || dismissed || hasSeenDialog()) {
    return null;
  }

  const handleDismiss = () => {
    markDialogSeen();
    setDismissed(true);
  };

  const handleInstall = async () => {
    markDialogSeen();
    setDismissed(true);
    await promptInstall();
  };

  return (
    <Dialog
      hidden={false}
      onDismiss={handleDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: t('Pwa.install.dialogTitle'),
        subText: t('Pwa.install.dialogBody')
      }}>
      <DialogFooter>
        <DefaultButton onClick={handleDismiss} text={t('Pwa.install.notNow')} />
        <PrimaryButton onClick={handleInstall} text={t('Pwa.install.installNow')} />
      </DialogFooter>
    </Dialog>
  );
};

export default PwaInstallDialog;
